// Catálogo de tools que se le pasa al agente en Interfaz V2 (el asistente y el Chat).
//
// La descripción de cada tool es su CONTRATO: parámetros, qué devuelve, y el "cuándo
// sí / cuándo no" en una línea. El "cuándo" entre herramientas parecidas vive en el
// MAPA del system prompt (`agentSystemPrompt.ts`) y el "cómo" de cada tarea en las
// skills (`skills/fabrica/*.md`) — ADR-2026-09-09-el-prompt-es-un-mapa. Si una regla
// ya está en otra capa, no la repitas acá: dos copias terminan contradiciéndose.
//
// Cada tool acá tiene su match en `executeTool` dentro de `useV2Chat.ts`.
// Si agregás una, agregala en ambos lados y sumala al MAPA (hay un test que lo exige).

import {
  DESCRIPCION_DE_ETIQUETA,
  DESCRIPCION_DE_FONDO_BLANCO,
  DESCRIPCION_DE_LUZ_FONDO_BLANCO,
  DESCRIPCION_DE_PIEL_EXPUESTA,
  DESCRIPCION_DE_EDICION,
  DESCRIPCION_DE_TAREA,
  REGLAS_DE_IMAGEN,
} from './reglasDeImagen';

export type V2Tool = {
  name: string;
  description: string;
  input_schema: {
    type: 'object';
    properties: Record<string, any>;
    required?: string[];
  };
};

export const V2_AGENT_TOOLS: V2Tool[] = [
  {
    name: 'save_memory',
    description:
      'Guarda un dato DURABLE sobre el usuario o su marca para recordarlo en futuras conversaciones (una preferencia fija, info del negocio, su tono, su público, etc.). Se ejecuta en SILENCIO (no muestra UI ni interrumpe). USALA SOLO para cosas que valga la pena recordar a largo plazo — NUNCA para detalles del pedido actual ni cosas efímeras. Si el dato actualiza/reemplaza algo que ya sabías, pasá el MISMO `key` para sobrescribir en vez de duplicar. No confirmes en voz alta que guardaste algo salvo que el usuario lo pida explícitamente.',
    input_schema: {
      type: 'object',
      properties: {
        content: {
          type: 'string',
          description:
            'El dato a recordar, en una frase clara y autocontenida. Ej: "Prefiere fondos blancos minimalistas" / "Su público son madres jóvenes de 25-35".',
        },
        key: {
          type: 'string',
          description:
            'OPCIONAL. Identificador corto y estable para datos que se actualizan con el tiempo (ej "tono_de_voz", "publico_objetivo", "estilo_visual"). Si ya existe una memoria con ese key, se sobrescribe. Omitilo para hechos sueltos que no se reemplazan.',
        },
      },
      required: ['content'],
    },
  },
  // ── Skills (ADR-2026-09-09-el-agente-tiene-skills) ────────────────────────
  {
    name: 'cargar_skill',
    description:
      'Carga el INSTRUCTIVO completo de una SKILL: cómo hacer bien un tipo de tarea (escribir el prompt de una foto con modelo, encarar un análisis de competencia, leer una planilla grande, planificar un pedido de varios pasos). Las que tenés están listadas en "SKILLS DISPONIBLES" del system prompt con su descripción y cuándo usarla; el cuerpo solo llega cuando la cargás.\n' +
      'Es silenciosa, gratis y no interrumpe: llamala en la MISMA ronda que otras herramientas (dos skills a la vez, o una skill junto con ver_productos). Cargá la skill ANTES de encarar la tarea que describe, no después de hacerla mal. Si ya la cargaste en esta conversación y su texto sigue en el historial, no la vuelvas a cargar. Devuelve `{ texto }` con el instructivo; si el nombre no existe, te lista los que sí.',
    input_schema: {
      type: 'object',
      properties: {
        nombre: {
          type: 'string',
          description: 'El nombre exacto de la skill, tal cual figura en SKILLS DISPONIBLES (ej. "foto-con-modelo").',
        },
      },
      required: ['nombre'],
    },
  },
  {
    name: 'guardar_skill',
    description:
      'Guarda una SKILL DE LA EMPRESA: un procedimiento repetible que el usuario te enseñó ("siempre que te pida fotos para Instagram, hacelas así"). Es silenciosa. Queda en SKILLS DISPONIBLES a partir del próximo turno y se carga con cargar_skill como cualquier otra. Con el mismo `nombre` se sobrescribe; los nombres de las skills de fábrica no se pueden usar.\n' +
      'ANTES de escribir una, cargá la skill `crear-skill`: dice cuándo vale la pena y cómo se escribe para que sirva la próxima vez. NO es para datos sueltos de la marca o el usuario (eso es save_memory) ni para el pedido de hoy. Devuelve `{ saved, nombre }` o `{ saved: false, motivo }`.',
    input_schema: {
      type: 'object',
      properties: {
        nombre: {
          type: 'string',
          description: 'Corto y descriptivo, en kebab-case: "fotos-para-instagram", "presupuesto-mayorista".',
        },
        descripcion: {
          type: 'string',
          description: 'Una frase: qué enseña. Es lo que vas a leer en cada turno para decidir si cargarla.',
        },
        cuando: {
          type: 'string',
          description: 'El disparador concreto: en qué situación cargarla. Ej: "Cuando pide fotos para Instagram".',
        },
        cuerpo: {
          type: 'string',
          description:
            'El instructivo en markdown: título, pasos numerados (con la herramienta de cada uno), reglas, un ejemplo si hace falta. Escrito para alguien que no vio esta conversación.',
        },
      },
      required: ['nombre', 'descripcion', 'cuando', 'cuerpo'],
    },
  },
  {
    name: 'activar_investigacion',
    description:
      'Habilita los SCRAPERS de datos reales (Instagram/Facebook/Google Maps/tiendas online, vía Apify), que están APAGADOS por defecto porque pueden consumir varios créditos. Al llamarla, al USUARIO le aparece un cartel de CONFIRMACIÓN (le avisa que puede tardar un momento y consumir varios créditos) con Aceptar/Cancelar. Mirá el resultado: si devuelve `{ activado: true }` el usuario aceptó → en tu PRÓXIMO turno ya podés usar los scrapers (queda habilitado para todo el chat); si devuelve `{ activado: false, cancelado: true }` el usuario canceló → NO scrapees, decíselo en una línea y ofrecé otra cosa. Llamala (sola, sin batchear) SOLO cuando vas a traer datos reales de una cuenta/marca/tienda/lugar concretos. La BÚSQUEDA WEB (web_search) NO necesita esto: está siempre disponible.',
    input_schema: {
      type: 'object',
      properties: {
        objetivo: {
          type: 'string',
          description:
            'OPCIONAL. En una línea, qué vas a investigar (ej: "Instagram de un competidor", "precios de una tienda", "tendencias del rubro"). Solo para contexto/registro.',
        },
      },
    },
  },
  {
    name: 'generate_audio',
    description:
      'Genera AUDIO a partir de texto: una locución/voz en off (TTS), música o un efecto de sonido. Mostrá el resultado reproducible en el chat. Usala cuando el usuario pide un audio, una voz, una narración, un jingle, música o un sonido (una puerta, aplausos, un whoosh, un ambiente). Devuelve `{ confirmed: true }`.',
    input_schema: {
      type: 'object',
      properties: {
        text: {
          type: 'string',
          description:
            'Para voz: el texto EXACTO a locutar (el guion). Para música: una descripción del estilo/mood. Para un efecto: qué suena, concreto ("puerta de madera que se cierra fuerte"). Obligatorio.',
        },
        audio_type: {
          type: 'string',
          enum: ['voz', 'musica', 'efecto'],
          description: 'Tipo de audio: "voz" (locución TTS, default), "musica" o "efecto" (efecto de sonido, 1 crédito).',
        },
        duracion_seg: {
          type: 'number',
          description:
            'Música: cuántos segundos dura (3 a 300; si no la piden, 30). Sale exacto con ese largo y cuesta 1 crédito cada 30 s: si piden una duración, respetala. Efecto: de 0,5 a 30 s; si no la piden, no la pases (la elige el modelo).',
          minimum: 0.5,
          maximum: 300,
        },
        autogenerate: {
          type: 'boolean',
          description:
            'Si true, se genera solo apenas aparece la card (sin click). Poné true cuando ya tenés claro el texto/estilo.',
        },
      },
      required: ['text'],
    },
  },
  {
    name: 'generate_video',
    description:
      'Genera un VIDEO corto. Mostrá el resultado reproducible en el chat. La card muestra un RESUMEN de lo que se va a generar (prompt, formato, duración, calidad, resolución, frames, referencias) con el costo en créditos. Si espera el click o arranca sola depende de los PERMISOS del usuario — te los digo en "QUÉ TENÉS HABILITADO AHORA": escribí tu texto según eso, ANTES de llamar la tool. Devuelve `{ confirmed: true, video_url, first_frame_image_id?, last_frame_image_id? }` — esos image_id son las imágenes de los frames que se generaron/reusaron; cuando lo leas, el video YA está hecho: contá el resultado, nunca pidas que confirme algo que ya pasó.\n' +
      'PODÉS controlar FRAMES y REFERENCIAS (muy recomendado para fidelidad):\n' +
      '• PRIMER y/o ÚLTIMO frame (`first_frame`/`last_frame`): SOLO tres fuentes — (a) GENERAR una imagen a propósito (source "generar" + un prompt de imagen): anima esa imagen y la respeta mucho más; (b) una imagen ADJUNTA del usuario (source "adjunto"); (c) una imagen YA GENERADA en este chat (source "generada" + `image_id` — el id que devolvió la tool que la generó). PROHIBIDO usar la foto cruda de un producto/modelo/logo como frame — NO son imágenes aptas de frame. Si querés el producto/modelo/logo en el frame, generá una imagen que lo describa (source "generar") y pasá su foto real en `reference_images` para que la imagen le sea fiel.\n' +
      '• REFERENCIAS (`reference_images`): imágenes que GUÍAN el look/estilo del video sin fijar frames — acá SÍ van producto (product_id), modelo (model_id), logo, adjunto o una imagen generada del chat (source "generada" + image_id). Las imágenes adjuntas del usuario entran solas además.\n' +
      '• PERSONA HABLANDO (testimonio, presentador, unboxing…): video normal; cada beat con habla lleva Says: "..." con EXACTAMENTE lo que pronuncia (líneas cortas, 5-6 palabras por segundo). El idioma es una DIRECCIÓN de escena, fuera de las comillas y antes del primer Says: literal "She speaks in Argentine Spanish (En español argentino)" salvo que el usuario haya pedido otro; adentro de las comillas la persona lo DICE. Lo que pide el usuario es guía para vos, no diálogo: sólo son palabras habladas las que dio como tales. El detalle está en la skill `video`.\n' +
      '• CALIDAD (`quality`): "profesional" (default, mejor modelo, hasta 30s) o "basica" (más barata, hasta 15s). RESOLUCIÓN (`resolution`): 480p/720p/1080p (default 720p). Costo por segundo en créditos: profesional 2/3/5 · básica 1/2/4 (480p/720p/1080p). El usuario puede cambiar ambas en la card. Preseleccioná "basica" SOLO si el usuario pidió abaratar / la opción más económica, y una resolución SOLO si la pidió puntualmente. En profesional, 480p es un BORRADOR: sale barato para probar la idea y después se puede pasar a 1080p con la misma toma (finalizar_borrador_de_video, 7 días). Cuando el usuario quiera iterar, probar o abaratar, proponé 480p profesional.\n' +
      '• VIDEO ADJUNTO (`reference_video`): si el pedido es sobre un video adjunto ("editalo", "cambiale…", "que tenga más tomas", "copialo"), el video ENTERO viaja al modelo como referencia, SIEMPRE, y el `prompt` deja de ser el de creación (NADA de beats): es una lista de CAMBIOS sobre ese video. Tres modos: "editar" = ese mismo video con el cambio pedido (producto, frase, fondo, ropa), lo demás idéntico; "camara" = ese mismo video con otras tomas, ángulos o cortes (calidad profesional); "copiar" = un video COMO ese pero nuevo: misma estructura, tiempos y cámara, con OTRA persona, OTRO lugar y otros objetos. Nunca frames del propio video como referencia ni un video "nuevo" como alternativa a editar. Cuarto modo, "referencia", SÓLO si el usuario pide textualmente usar un video adjunto como referencia de un video NUEVO ("usá este video de referencia"): el prompt es el normal por beats y el video guía estilo, ritmo y cámara; nunca lo elijas vos en lugar de editar o copiar. El modo pone la calidad por default (básica en editar y copiar, profesional en camara: es la que corta entre ángulos) y el usuario la puede cambiar en la card en cualquier modo. TOPES DEL VIDEO DE ENTRADA: 14 s en básica y 29 s en profesional (uno menos que la salida: 15 s de entrada en básica se lee como más de 15 y se rechaza). Cargá antes la skill `copiar-un-video`.\n' +
      'Si no pasás frames, se anima directo desde el prompt (las referencias igual guían el look).\n' +
      'IMPORTANTE: TODO el video sale de UNA sola llamada a esta tool. NUNCA llames generate_image como "paso previo" de un video — esa imagen suelta NO se conecta al video; la imagen de arranque se genera ADENTRO (first_frame source "generar"), y una imagen que ya existe en el chat se referencia con source "generada".\n' +
      'CORREGIR UN VIDEO QUE YA SALIÓ: se trabaja sobre ese video con `reference_video` (si salió de este chat, que lo agregue con «Agregar» en la barra): el producto, una frase o el fondo → modo "editar"; otras tomas, ángulos, cortes o movimiento de cámara → modo "camara" (calidad profesional, decí el costo). Sólo otra luz, otra duración o una idea distinta es un video NUEVO que cobra de nuevo y sale distinto: reusá el frame (first_frame {source: "generada", image_id: el first_frame_image_id que devolvió la llamada anterior}) y DECILE antes, con el costo, que es otra generación — nunca "lo reedito". Si es la voz o una palabra: el botón «Corregir voz» de la barra del video (en el chat y en el Estudio), gratis, sin regenerar. Si es recortar, alargar, unir o musicalizar: el Estudio («Editar con Estudio»). Detalle en la skill `video` §6.',
    input_schema: {
      type: 'object',
      properties: {
        prompt: {
          type: 'string',
          description:
            'El prompt FINAL que lee el generador, escrito con la skill `video` (más `video-ugc` o `video-anuncio` si el pedido es de un tipo conocido): en inglés, por beats con su rango de tiempo que suman la duración, imágenes por @ImageN en el orden de `reference_images`, Says: "..." sólo cuando alguien habla. Obligatorio. OJO: si pasás `first_frame`, el video ARRANCA de esa imagen — los beats describen solo el MOVIMIENTO/acción/cámara a partir de ese cuadro (ej: "camina hacia cámara, la tela fluye con el paso, la cámara la sigue suave"). Re-describir la escena puede hacer que el video se despegue del frame.',
        },
        format: {
          type: 'string',
          description:
            'Formato de salida (slug: post / square / story / portrait / banner). Default story (9:16).',
        },
        duration: {
          type: 'number',
          description:
            'Duración en segundos (default 5). Tope según calidad: 15 s en "basica", 30 s en "profesional". Con `reference_video`, el video de ENTRADA tiene su propio tope: 14 s en básica y 29 s en profesional.',
        },
        quality: {
          type: 'string',
          enum: ['basica', 'profesional'],
          description:
            'Calidad del video (default "profesional": mejor modelo, hasta 30s, 2/3/5 créditos/seg según resolución). "basica" es la opción económica (hasta 15s, 1/2/4). Pasá "basica" SOLO si el usuario pidió abaratar; si no, omití el campo (el usuario elige en la card).',
        },
        resolution: {
          type: 'string',
          enum: ['480p', '720p', '1080p'],
          description:
            'Resolución de salida (default "720p"). Cambia el costo por segundo (profesional: 480p=2 · 720p=3 · 1080p=5 créditos; básica: 1/2/4). Pasala SOLO si el usuario pidió una resolución puntual; si no, omitila (el usuario elige en la card).',
        },
        first_frame: {
          type: 'object',
          description:
            'OPCIONAL. Imagen con la que ARRANCA el video. Solo "generar", "adjunto" o "generada" — nunca fotos crudas de producto/modelo/logo (esas van en `reference_images`).',
          properties: {
            source: {
              type: 'string',
              enum: ['generar', 'adjunto', 'generada'],
              description:
                '"generar" (creás una imagen apta con `prompt`), "adjunto" (imagen que adjuntó el usuario), "generada" (imagen YA generada en este chat, `image_id`).',
            },
            prompt: {
              type: 'string',
              description: 'Si source="generar": el prompt de la imagen a generar.',
            },
            image_id: {
              type: 'string',
              description:
                'Si source="generada": id de la imagen generada en el chat (lo devolvió la tool que la generó).',
            },
            adjunto_id: {
              type: 'string',
              description:
                'Con source="adjunto": una imagen que el usuario subió en un mensaje ANTERIOR, por el id que dice el marcador de ese mensaje ("adjunto_id=m1-2"). Sin esto, "adjunto" es la primera imagen adjunta de ESTE mensaje.',
            },
          },
          required: ['source'],
        },
        last_frame: {
          type: 'object',
          description:
            'OPCIONAL. Imagen con la que TERMINA el video (mismo shape que first_frame). Útil para un morph/transición de A → B.',
          properties: {
            source: {
              type: 'string',
              enum: ['generar', 'adjunto', 'generada'],
            },
            prompt: { type: 'string' },
            image_id: { type: 'string' },
            adjunto_id: { type: 'string' },
          },
          required: ['source'],
        },
        reference_video: {
          type: 'object',
          description:
            'OPCIONAL. El VIDEO ADJUNTO que va al modelo como referencia. OMITÍ `duration`: el resultado dura lo mismo que el video desde `desde`, con el tope de ENTRADA de la calidad (14 s en básica, 29 s en profesional), que es lo que se espera al editar. Pasala SÓLO si el usuario pidió un tramo más corto. `desde` = segundo del original donde arranca (default 0). Con esto no van frames; la calidad la pone el modo por default (básica; profesional en "camara") y el usuario la cambia en la card. Las fotos del producto siguen yendo en `reference_images` (source "producto"). El `prompt` es una lista de cambios, no una descripción por beats. `archivo` es interno: al usuario no le repitas el nombre del archivo ni ningún id.',
          properties: {
            modo: {
              type: 'string',
              enum: ['editar', 'camara', 'copiar', 'referencia'],
              description:
                '"editar": es SU video y cambia sólo lo que pide (misma persona, mismo lugar, misma cámara). "camara": SU video filmado de otra forma: más tomas, otros ángulos, cortes, un plano detalle; misma persona, lugar y frases; va en calidad profesional. "copiar": quiere un video como ese pero distinto: misma estructura, otra persona, otro lugar y otros objetos, más sus cambios. "referencia": SÓLO a pedido textual, un video NUEVO que toma del adjunto el estilo, el ritmo y la cámara (prompt normal por beats). Default "editar".',
            },
            archivo: {
              type: 'string',
              description: 'Nombre del video adjunto, tal cual figura en el contexto. Omitilo si hay uno solo.',
            },
            desde: {
              type: 'number',
              description: 'Segundo del video original donde arranca el tramo a copiar (default 0). Si el video pasa del tope de entrada por menos de 2 s, dejalo en 0: se corta el final, no el comienzo.',
            },
          },
        },
        reference_images: {
          type: 'array',
          description:
            'OPCIONAL. Imágenes de REFERENCIA que guían el look/estilo del video sin fijar frames. Acá SÍ van producto/modelo/logo directos, y también imágenes ya generadas del chat. Las adjuntas del usuario en ESTE mensaje entran automáticamente (no hace falta listarlas); listá una con source "adjunto" sólo para ponerle `etiqueta`. Las de mensajes ANTERIORES no viajan solas: pasalas con source "adjunto" + `adjunto_id` (el id del marcador de ese mensaje) — es el caso de "subió las fotos y después dijo «dale»". Al final del prompt se agrega sola una línea que dice qué es cada @Image, con la etiqueta si la tiene ("@Image2 es la pose de referencia (de perfil)").',
          items: {
            type: 'object',
            properties: {
              source: {
                type: 'string',
                enum: ['producto', 'modelo', 'logo', 'adjunto', 'generada'],
                description:
                  '"producto" (foto de un producto, `product_id`), "modelo" (`model_id`), "logo" (el logo de la marca), "adjunto" (imagen que adjuntó el usuario), "generada" (imagen ya generada en el chat, `image_id`).',
              },
              adjunto_id: {
                type: 'string',
                description:
                  'Con source="adjunto": una imagen que el usuario subió en un mensaje ANTERIOR, por el id que dice el marcador de ese mensaje ("adjunto_id=m1-2"). Ya no la ves, pero sigue guardada y viaja como referencia sin que la vuelva a adjuntar; sumale `etiqueta` con qué es ("Producto: mocasín chocolate").',
              },
              product_id: {
                type: 'string',
                description: 'Si source="producto": id del producto del catálogo.',
              },
              model_id: {
                type: 'string',
                description: 'Si source="modelo": id del modelo del catálogo.',
              },
              image_id: {
                type: 'string',
                description:
                  'Si source="generada": id de la imagen generada en el chat.',
              },
              etiqueta: {
                type: 'string',
                description:
                  'QUÉ ES la imagen, cuando la fuente no lo dice: una foto ADJUNTA o GENERADA que en realidad es el producto, la persona, la pose, el fondo o el logo, o algo que el prompt necesita nombrar. Si empieza con "Producto", "Persona" (o "Modelo"), "Pose", "Fondo" (o "Escena"), "Logo" o "Plantilla" (una pieza gráfica de la marca: se conserva su diseño y el contenido sale del producto), la línea de referencias la nombra como esa cosa, con el resto entre paréntesis: "Pose: chica caminando", "Producto: remera negra" (dos fotos con el mismo nombre son vistas del mismo producto), "Persona: la del vestido", "Fondo: terraza", "Plantilla: lámina de la marca". Cualquier otro texto se nombra tal cual: "Estampa a copiar". Vacío = lo que dice la fuente. Corta (hasta 60 caracteres).',
              },
              imagen: {
                type: 'integer',
                description:
                  'Con source="adjunto" y varias adjuntas en el mensaje: a cuál va la `etiqueta`, por su número ("Imagen 2" → 2). Con una sola adjunta no hace falta.',
              },
            },
            required: ['source'],
          },
        },
      },
      required: ['prompt'],
    },
  },
  {
    name: 'finalizar_borrador_de_video',
    description:
      'Pasa a 1080p un BORRADOR (un video Profesional generado en 480p en los últimos 7 días): sale LA MISMA toma en alta, como una creación nueva. Cuesta 5 créditos por segundo del borrador. Llamala primero con confirmado: false para que te diga el costo, decíselo al usuario y, si en su mensaje SIGUIENTE dice que sí, llamala con confirmado: true (en el mismo turno no vale: se verifica lo que escribió la persona). El video_id es el que devolvió generate_video o show_recent_media. Un video que no es borrador, ya pasado a 1080p o vencido no se puede pasar: proponé regenerar.',
    input_schema: {
      type: 'object',
      properties: {
        video_id: { type: 'string', description: 'Id del video borrador (fila de Creaciones).' },
        confirmado: {
          type: 'boolean',
          description: 'true SÓLO después del OK explícito del usuario con el costo a la vista.',
        },
      },
      required: ['video_id', 'confirmado'],
    },
  },
  {
    name: 'generate_image',
    description:
      'Genera imágenes a partir de un prompt libre. Es el ÚNICO camino de imagen que tenés: sirve para TODO — escenas, conceptos, banners, fondos, ilustraciones, ediciones creativas Y TAMBIÉN para "mi producto puesto sobre una persona" (foto lifestyle/editorial de su producto). Usá `pick_product` antes solo si el usuario quiere SU producto real y no sabés cuál es; con el id elegido, llamá esta tool con ese producto en `reference_images`. Mostrá una card con el prompt y un botón Generar. Devuelve `{ confirmed: true }`.\n' +
      REGLAS_DE_IMAGEN,
    input_schema: {
      type: 'object',
      properties: {
        prompt: {
          type: 'string',
          description:
            'Prompt detallado de la imagen a generar, en base a lo que pidió el usuario (traducí su pedido a una descripción visual rica y clara). Si aparece una persona, describila como un modelo humano REAL y fotorrealista (nunca un maniquí).',
        },
        format: {
          type: 'string',
          description:
            'Formato de salida (slug: post / square / story / portrait / banner, o un slug `perfil:…` de un formato personalizado del usuario, tal cual te lo doy). Default el último formato usado por el usuario.',
        },
        cantidad: {
          type: 'number',
          description:
            'OPCIONAL. Cuántas imágenes generar en una sola tanda (1 a 6, default 1). Usalo cuando el usuario pide varias opciones/variantes ("dame 3 opciones", "hacé 4 fotos"). Cada imagen cuesta créditos aparte, así que NO infles este número por tu cuenta: solo lo que el usuario pidió.',
        },
        quality: {
          type: 'string',
          enum: ['1K', '2K', '4K'],
          description:
            'OPCIONAL. Resolución de salida: 2K es el default y cuesta 1 crédito, igual que 1K (más liviana); 4K cuesta 2. Pasala SOLO si el usuario pidió otra para ESTA imagen ("en 4K", "máxima resolución", "para imprimir", "para una gráfica grande", "más liviana"); si no, omitila — el sistema ya aplica la preferencia del usuario y él la puede cambiar en la card. Si lo que quiere es un default para todo lo que venga, eso NO va acá: usá `configurar_preferencias`.',
        },
        tarea: {
          type: 'string',
          enum: ['crear', 'editar', 'componer', 'grafica', 'explorar', 'cine'],
          description: DESCRIPCION_DE_TAREA,
        },
        edicion: {
          type: 'string',
          enum: ['conservar', 'fusionar'],
          description: DESCRIPCION_DE_EDICION,
        },
        piel_expuesta: {
          type: 'boolean',
          description: DESCRIPCION_DE_PIEL_EXPUESTA,
        },
        fondo_blanco: {
          type: 'boolean',
          description: DESCRIPCION_DE_FONDO_BLANCO,
        },
        luz_fondo_blanco: {
          type: 'string',
          enum: ['difusa', 'dura'],
          description: DESCRIPCION_DE_LUZ_FONDO_BLANCO,
        },
        reference_images: {
          type: 'array',
          description:
            'OPCIONAL (máx 10). Imágenes de REFERENCIA que la generación debe respetar — acá van el producto (`product_id`), modelo (`model_id`), logo, una imagen ya generada del chat (`image_id`), una POSE guardada (`pose_id`) o un FONDO guardado (`fondo_id`). Las adjuntas del usuario en ESTE mensaje entran automáticamente (no hace falta listarlas); las de mensajes anteriores no viajan solas: pasalas con source "adjunto" + `adjunto_id`. Los ids de pose y fondo salen de `mostrar_guardado` (tipo "poses" / "fondos") o del [registro:] de un mensaje anterior donde el usuario los adjuntó: con eso podés REUSAR la pose que enganchó más arriba sin pedirle que la vuelva a adjuntar. Si pasás una referencia, el `prompt` la NOMBRA por su rótulo y NO la describe ("Replicá la postura de `Pose de referencia:`", no "de pie con una mano en la cadera"): describirla compite con la foto. Si NO pasás ninguna, el prompt NO debe mencionar imágenes de referencia.',
          items: {
            type: 'object',
            properties: {
              source: {
                type: 'string',
                enum: ['producto', 'modelo', 'logo', 'adjunto', 'generada', 'pose', 'fondo'],
                description:
                  '"producto" (foto real de un producto, `product_id`), "modelo" (`model_id`), "logo" (el logo de la marca), "adjunto" (imagen que adjuntó el usuario), "generada" (imagen ya generada en el chat, `image_id`), "pose" (pose guardada, `pose_id`), "fondo" (fondo guardado, `fondo_id`).',
              },
              product_id: {
                type: 'string',
                description: 'Si source="producto": id del producto del catálogo.',
              },
              model_id: {
                type: 'string',
                description: 'Si source="modelo": id del modelo del catálogo.',
              },
              image_id: {
                type: 'string',
                description:
                  'Si source="generada": id de la imagen generada en el chat.',
              },
              pose_id: {
                type: 'string',
                description:
                  'Si source="pose": id de una pose guardada (de `mostrar_guardado` tipo "poses", o del [registro:] de un mensaje donde el usuario la adjuntó).',
              },
              fondo_id: {
                type: 'string',
                description:
                  'Si source="fondo": id de un fondo guardado (de `mostrar_guardado` tipo "fondos", o del [registro:] de un mensaje donde el usuario lo adjuntó).',
              },
              etiqueta: {
                type: 'string',
                description: DESCRIPCION_DE_ETIQUETA,
              },
              imagen: {
                type: 'integer',
                description:
                  'Con source="adjunto" y varias adjuntas en el mensaje: a cuál va la `etiqueta`, por su número ("Imagen 2" → 2). Con una sola adjunta no hace falta.',
              },
              adjunto_id: {
                type: 'string',
                description:
                  'Con source="adjunto": una imagen que el usuario subió en un mensaje ANTERIOR, por el id que dice el marcador de ese mensaje ("adjunto_id=m1-2"). Ya no la ves, pero sigue guardada y viaja como referencia sin que la vuelva a adjuntar; sumale `etiqueta` con qué es ("Producto: bikini rojo").',
              },
            },
            required: ['source'],
          },
        },
      },
      required: ['prompt'],
    },
  },
  {
    name: 'pick_product',
    description:
      'Muestra inline en el chat la UI para que el usuario elija uno o varios productos de su catálogo, o suba uno nuevo. USÁ ESTA TOOL SIEMPRE que necesites un producto para la generación, NUNCA pidas el producto en texto. Si el usuario describió un producto en su mensaje (ej: "una remera blanca"), intentá matchear su descripción contra el catálogo y pasá ese ID en `preselected_ids` — el usuario lo va a ver marcado con check. IMPORTANTE: si la descripción matchea VARIOS productos del mismo tipo (ej: "las zapatillas" y hay varias), NO los preselecciones a todos — dejá `preselected_ids` vacío y pedile que elija cuál (sino lo obligás a destildar). Si no podés matchear con confianza, mostrá la UI igual con un mensaje pidiendo que suba la foto. Devuelve `{ product_ids: string[] }` con los IDs elegidos (puede ser uno o varios).',
    input_schema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description:
            'Texto breve (1 línea) que aparece arriba de la UI explicando qué necesitás. Ej: "Elegí el producto" / "Subí la foto de la remera" / "Confirmá que sea esta prenda".',
        },
        preselected_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'IDs del catálogo que vienen pre-marcados con check. Usalo SÓLO cuando hay UN match claro y único (el usuario nombró un producto y matchea uno solo). Dejá vacío si hay varios candidatos o si el usuario tiene que elegir desde cero.',
        },
        candidate_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'IDs candidatos que se muestran en la grilla SIN tildar, para que el usuario elija cuál. Usalo cuando el usuario describió un tipo de producto que matchea VARIOS (ej: "el vestido" y tiene varios vestidos): pasá acá esos IDs y dejá preselected_ids vacío — así ve los vestidos y toca el que quiere, sin tener que abrir "Elegir de mis productos".',
        },
        allow_multi: {
          type: 'boolean',
          description:
            'Si true, el usuario puede elegir varios productos a la vez. Default true. Poné false sólo si el agente está seguro de que tiene que ser uno solo.',
        },
      },
      required: ['message'],
    },
  },
  {
    name: 'pick_creacion',
    description:
      'Muestra inline en el chat la UI para que el usuario elija SOBRE QUÉ PIEZA trabajar: una imagen que ya generó o un producto de su catálogo. Usala CUANDO el usuario pide cambiar / retocar / rehacer / variar algo que YA EXISTE ("hacela más clara", "cambiale el fondo", "sacale el texto", "otra igual pero de noche", "cambiá la foto de la remera") y NO sabés con certeza a cuál se refiere. La card sugiere sola: trae las últimas imágenes generadas del usuario y, si hay una sola, la muestra grande para confirmar de un toque; además siempre ofrece el historial completo y el catálogo de productos, así el usuario llega a cualquier pieza sin salir del chat.\n' +
      'Devuelve `{ tipo: "creacion", image_id }` o `{ tipo: "producto", product_id }`. Con eso llamá `generate_image` pasando esa pieza en `reference_images` (source "generada" + `image_id`, o source "producto" + `product_id`) y el prompt describiendo EL CAMBIO pedido sobre esa referencia.\n' +
      'CUÁNDO NO: si el usuario acaba de generar algo en este mismo chat y dice "esa", ya sabés cuál es — pasala directo, no preguntes. Si el pedido es una imagen NUEVA (no una variación de algo existente), tampoco: eso es generate_image a secas, o pick_product si hace falta su producto real.',
    input_schema: {
      type: 'object',
      properties: {
        message: {
          type: 'string',
          description:
            'Texto breve (1 línea) arriba de la UI. Ej: "¿Cuál querés retocar?" / "¿Es esta la remera?".',
        },
        fuente: {
          type: 'string',
          enum: ['creacion', 'producto', 'ambas'],
          description:
            '"creacion" si el usuario habla de algo que GENERÓ (una imagen del chat/historial), "producto" si habla de un producto de su catálogo, "ambas" (default) si no está claro.',
        },
        limit: {
          type: 'number',
          description:
            'Cuántas generaciones recientes mostrar como candidatas (1 a 9, default 4). Poné 1 si estás casi seguro de que es la última.',
        },
      },
      required: ['message'],
    },
  },
  {
    name: 'preguntar',
    description:
      'Muestra inline en el chat un CUESTIONARIO con opciones para tocar, en vez de preguntar en prosa. Usala CADA VEZ que te falte información para generar bien (para qué red, qué tono, con o sin modelo, qué estilo, qué le tiene que pasar al espectador) o cuando tengas una duda con respuestas acotadas. Podés mandar UNA pregunta o VARIAS de una sola vez (mejor varias juntas que un ping-pong de mensajes: el usuario las contesta todas en un toque). Cada pregunta es de opción única (default) o `multiple: true` si tiene sentido elegir más de una. Cada una lleva una escotilla "Otra" para que el usuario escriba lo suyo, así que no agregues vos una opción "otro". Devuelve `{ respuestas: [{ id, pregunta, elegidas: string[] }] }` — leé eso y seguí, NO vuelvas a preguntar lo mismo en texto ni agradezcas la respuesta con un mensaje vacío.\n' +
      'CUÁNDO NO: si la pregunta es abierta y no tenés opciones razonables (ej "contame de tu marca"), escribila en texto. Si lo que falta es el PRODUCTO, la POSE o el FORMATO, usá su tool (`pick_product`, etc.), que muestran la UI real. Y no la uses para confirmar algo que ya sabés: 3 a 5 opciones por pregunta, máximo 4 preguntas — un formulario largo espanta más que una pregunta bien hecha.',
    input_schema: {
      type: 'object',
      properties: {
        intro: {
          type: 'string',
          description:
            'OPCIONAL. Una línea arriba del cuestionario que dice para qué preguntás. Ej: "Para afinar la foto, contame dos cosas".',
        },
        preguntas: {
          type: 'array',
          description: 'Las preguntas, en orden. Máximo 4.',
          items: {
            type: 'object',
            properties: {
              id: {
                type: 'string',
                description:
                  'OPCIONAL. Identificador corto y estable (ej "red", "tono"). Vuelve en la respuesta; si no lo mandás se numeran solas.',
              },
              pregunta: {
                type: 'string',
                description:
                  'La pregunta, corta y en criollo. Ej: "¿Para qué red es?".',
              },
              opciones: {
                type: 'array',
                items: { type: 'string' },
                description:
                  'Las opciones, 3 a 5. Etiquetas cortas (1-3 palabras) — son pills, no oraciones.',
              },
              multiple: {
                type: 'boolean',
                description:
                  'true si el usuario puede elegir varias. Default false (una sola).',
              },
            },
            required: ['pregunta', 'opciones'],
          },
        },
      },
      required: ['preguntas'],
    },
  },
  {
    name: 'show_recent_media',
    description:
      'Muestra en el chat una GALERÍA con las ÚLTIMAS imágenes y videos que el usuario generó (las trae solas de su historial). Usala SOLO cuando el usuario pregunta por lo último que hizo / dónde quedó lo que generó (ej: "¿dónde está la última imagen que generé?", "mostrame lo último que hice", "no encuentro el video que hice recién"). NO la uses para mostrar algo que se acaba de generar en este mismo chat (eso ya aparece solo). Es de solo lectura: no cuesta créditos. Devuelve `{ shown: true }`. Después de llamarla, comentá en UNA línea corta que se las mostraste (ej "Acá tenés lo último que generaste") y ofrecé abrir el historial completo si quiere ver más. Si no había nada, la card lo dice sola.\n' +
      'IMPORTANTE (referencias): el sistema te deja un [registro:] con las piezas mostradas EN ORDEN y su image_id. Si después el usuario dice "animá la tercera", "usá la última", "esa" — mapealo a ese image_id y usá first_frame {source:"generada", image_id}. NUNCA le pidas el ID al usuario (es técnico y no lo sabe); si de verdad no podés distinguir cuál, preguntá en criollo ("¿la primera, la segunda…?") o por lo que se ve, jamás por id.',
    input_schema: {
      type: 'object',
      properties: {
        limit: {
          type: 'number',
          description:
            'Cuántas piezas traer (default 6, máximo 12). Usá 1-2 si el usuario pide puntualmente "la última".',
        },
      },
      required: [],
    },
  },
  {
    name: 'ver_lanzamiento',
    description:
      'Lee un LANZAMIENTO del usuario: sus productos con precio, stock, SKU, categorías, cuántas fotos tiene cada uno y cuáles son filas BORRADOR (cargadas por Excel, todavía sin producto del catálogo detrás). No tiene UI, no cuesta créditos y no interrumpe.\n' +
      'Sin `lanzamiento_id` usa el que el usuario tiene abierto, y si no, su borrador más reciente. Llamala SIEMPRE antes de editar: los `id` de cada línea son los que después mandás en `product_ids`, y los valores actuales son los que el usuario ve en pantalla.',
    input_schema: {
      type: 'object',
      properties: {
        lanzamiento_id: {
          type: 'string',
          description: 'Omitilo salvo que el usuario nombre otro lanzamiento.',
        },
      },
    },
  },
  {
    name: 'editar_lanzamiento',
    description:
      'Cambia datos de los productos DENTRO de un lanzamiento: precio, costo, stock, SKU, nombre, descripción, código de barras, marca, etiquetas, peso, medidas y SEO. Uno, varios o todos.\n' +
      'Llamá ANTES a ver_lanzamiento: necesitás los `id` y los valores actuales.\n' +
      'Cada cambio tiene `campo` y, o bien `valor` (fijar ese texto), o bien `operacion` para los numéricos: `{tipo:"multiplicar", valor:1.1}` sube 10%, `{tipo:"sumar", valor:500}`, `{tipo:"redondear", valor:990}` deja todos terminando en 990, `{tipo:"fijar", valor:4999}`. USÁ LA OPERACIÓN para los cambios masivos — calcular cuarenta precios vos es cómo se cuelan los errores.\n' +
      '`product_ids` acota a qué productos; sin él, aplica a TODOS los del lanzamiento (que es lo que pide "subí todos los precios"). Ojo: sin él es TODOS, así que si el usuario dijo "los de verano", pasá los ids.\n' +
      'La card muestra el diff (de qué valor a qué valor, producto por producto) y el usuario confirma. SIEMPRE confirma, aunque tenga los permisos en directo: un precio mal puesto no tiene vuelta atrás y termina publicado en su tienda. Tu texto: UNA línea diciendo qué proponés cambiar; no digas que ya está hecho.\n' +
      'Devuelve `{ applied, cambios, productos, salteados }`. Si vuelve `applied: false` con motivo "sin_cambios", los valores ya estaban así — decíselo, no lo intentes de nuevo.',
    input_schema: {
      type: 'object',
      properties: {
        lanzamiento_id: { type: 'string', description: 'Omitilo para el que está abierto.' },
        cambios: {
          type: 'array',
          description: 'Los cambios a aplicar.',
          items: {
            type: 'object',
            properties: {
              campo: {
                type: 'string',
                enum: [
                  'nombre',
                  'descripcion',
                  'precio',
                  'costo',
                  'stock',
                  'sku',
                  'codigoBarras',
                  'marca',
                  'tags',
                  'peso',
                  'alto',
                  'ancho',
                  'profundidad',
                  'seoTitulo',
                  'seoDescripcion',
                ],
              },
              valor: { type: 'string', description: 'El valor que queda. Para fijar texto.' },
              operacion: {
                type: 'object',
                description: 'Alternativa a `valor`, sólo para campos numéricos.',
                properties: {
                  tipo: { type: 'string', enum: ['fijar', 'multiplicar', 'sumar', 'redondear'] },
                  valor: { type: 'number' },
                },
                required: ['tipo', 'valor'],
              },
              product_ids: {
                type: 'array',
                items: { type: 'string' },
                description:
                  'Los `id` que te dio ver_lanzamiento. SIN esto el cambio va a TODOS los productos.',
              },
            },
            required: ['campo'],
          },
        },
        mensaje: {
          type: 'string',
          description: 'Una línea arriba de la card. Ej: "Subo 10% los precios de verano".',
        },
      },
      required: ['cambios'],
    },
  },
  {
    name: 'agregar_a_lanzamiento',
    description:
      'Suma productos a un lanzamiento. Dos fuentes:\n' +
      '1. Del CATÁLOGO: pasá `product_ids` (los del catálogo, no los del lanzamiento). Entran con su ficha; las fotos se mandan después por el camino de siempre.\n' +
      '2. Desde una PLANILLA adjunta: pasá `desde_planilla: true`. Cada fila se convierte en un producto del lanzamiento CON SUS DATOS Y SIN FOTOS. Las columnas se reconocen solas (nombre, precio, costo, stock, sku, código de barras, marca, peso, medidas, SEO): el usuario no tiene que renombrar nada.\n' +
      'Lo importante de la planilla: esas filas quedan esperando su producto. Cuando el usuario después mande el producto real con el MISMO SKU, se adopta esa fila y las fotos caen encima de los datos que ya cargó — no se duplica. Por eso el SKU es la columna que más importa: sin SKU, la fila no se puede atar a nada después. Si la planilla no tiene SKU, decíselo.\n' +
      'Una fila sin fotos NO se puede publicar todavía, y está bien: es media carga adelantada.\n' +
      'Tope: 50 productos por lanzamiento. La card muestra lo que va a entrar y el usuario confirma.',
    input_schema: {
      type: 'object',
      properties: {
        lanzamiento_id: { type: 'string', description: 'Omitilo para el que está abierto.' },
        product_ids: {
          type: 'array',
          items: { type: 'string' },
          description: 'Ids del CATÁLOGO. Para agregar productos que el usuario ya tiene.',
        },
        desde_planilla: {
          type: 'boolean',
          description: 'true = tomar las filas de la planilla que el usuario adjuntó.',
        },
        hoja: {
          type: 'string',
          description: 'Nombre de la hoja de la planilla. Omitilo para la primera.',
        },
        mensaje: { type: 'string', description: 'Una línea arriba de la card.' },
      },
    },
  },
  {
    name: 'ver_pdf',
    description:
      'Lee más páginas del PDF que el usuario adjuntó. Al adjuntarlo ya te llegó su forma y —si tiene capa de texto— el principio; llamá esto para el resto. No tiene UI, no cuesta créditos y no interrumpe.\n' +
      'DOS CASOS, y el resumen del adjunto te dice cuál es: (1) PDF con TEXTO (exportado de Word, Excel, un sistema): devuelve el texto de esas páginas, y podés pedir hasta 10 por vez. (2) PDF ESCANEADO o fotografiado (una factura del proveedor, un contrato firmado): no tiene texto, así que devuelve las páginas RENDERIZADAS como imagen y las LEÉS MIRÁNDOLAS. Ahí van de a 3 como mucho, porque cada página es una imagen que queda en todo el historial: pedí sólo las que necesites.\n' +
      'Si el PDF ya no está disponible (el usuario recargó la página), te lo dice: pedile que lo vuelva a adjuntar.\n' +
      'Si adjuntó VARIOS, decí cuál en `archivo` (el nombre sale del resumen de cada adjunto). Sin `archivo` se lee el último.',
    input_schema: {
      type: 'object',
      properties: {
        archivo: {
          type: 'string',
          description:
            'Nombre del PDF a leer, tal cual figura en su resumen ("[PDF adjunto: <nombre>]"). Omitilo si hay uno solo.',
        },
        desde: { type: 'number', description: 'Primera página a leer (empieza en 1). Default 1.' },
        paginas: {
          type: 'number',
          description: 'Cuántas leer. Hasta 10 si hay texto, hasta 3 si hay que renderizar.',
        },
        como: {
          type: 'string',
          enum: ['texto', 'imagen'],
          description:
            'Default "texto". Pedí "imagen" cuando el texto no alcanza para entender la página (un formulario, un plano, una tabla con layout raro). En un PDF escaneado siempre son imágenes, pidas lo que pidas.',
        },
      },
    },
  },
  {
    name: 'extraer_fotos_del_pdf',
    description:
      'Saca las FOTOS que vienen adentro de un PDF adjunto (una ficha técnica, un informe de inspección de fábrica, un catálogo del proveedor) y las guarda como adjuntos del chat. Te las muestra en hojas de contacto numeradas ("n · pX": número y página) y te dice qué adjunto_id ("pdf-…") es cada número. Esas fotos se usan igual que una que subió el usuario: reference_images con source "adjunto", agregar_productos con `adjunto_ids`, ver_fotos para mirar una grande. No tiene UI, no cuesta créditos.\n' +
      'Saltea logos, íconos y fotos repetidas. Un PDF escaneado no tiene fotos sueltas: ahí miralo con ver_pdf (como "imagen").\n' +
      'Para CREAR el producto elegí sólo fotos del producto entero (el producto solo o con lo que trae puesto): NUNCA la caja, etiquetas, manuales, bocetos, mediciones con instrumentos ni pruebas, aunque el usuario venda el kit en caja. Si es una ficha técnica o un informe de fábrica, cargá antes la skill `pdf` (cómo elegir fotos y qué datos guardar con guardar_datos_del_producto).',
    input_schema: {
      type: 'object',
      properties: {
        archivo: { type: 'string', description: 'Nombre del PDF, como en su resumen. Omitilo si hay uno solo.' },
        paginas: {
          type: 'array',
          items: { type: 'number' },
          description: 'Sólo estas páginas (empiezan en 1). Omitilo para todo el PDF (hasta 100 fotos).',
        },
      },
    },
  },
  {
    name: 'ver_video',
    description:
      'ANALIZA un video que el usuario adjuntó y te cuenta qué hay. Vos NO podés ver video: esta herramienta se lo manda a un modelo de video desde el backend y te devuelve el desglose en texto. Sin `pregunta` devuelve el análisis COMPLETO para copiar el estilo: la estructura toma por toma con tiempos (qué se ve, encuadre, movimiento de cámara, cuánto dura cada una), ritmo y transiciones, luz y paleta, personas y productos, textos en pantalla, el audio (locución transcripta, música, ambiente), el gancho de los primeros segundos y el cierre. Con `pregunta` contesta eso en particular ("¿qué dice la voz en off?", "¿qué productos aparecen?").\n' +
      'Cuesta créditos (poco: se cobra al costo, cerca de 1 crédito por minuto de video) y tarda entre 15 y 60 segundos, así que avisá en una línea que lo estás analizando. Llamala UNA vez por video y trabajá con lo que devuelve; repetila sólo con una pregunta nueva. Si adjuntó VARIOS, decí cuál en `archivo`. Si el video no está (no adjuntó ninguno), te lo dice: pedile que lo adjunte con el "+".\n' +
      'Después, lo normal es cargar la skill `copiar-un-video`: el video se EDITA mandándolo como `reference_video` de generate_video junto con el producto del usuario (misma persona, lugar y ritmo; cambia el producto y lo que dice). Decí que lo ANALIZASTE, no que lo viste.',
    input_schema: {
      type: 'object',
      properties: {
        archivo: {
          type: 'string',
          description:
            'Nombre del video, tal cual figura en el contexto del adjunto. Omitilo si hay uno solo.',
        },
        pregunta: {
          type: 'string',
          description:
            'Qué querés saber, si es algo puntual. Vacío = el desglose completo para copiar el estilo (lo normal).',
        },
      },
    },
  },
  {
    name: 'extraer_frames',
    description:
      'Saca hasta 6 CUADROS (frames) del video que el usuario adjuntó, en los segundos que pidas, y te los MUESTRA (los ves en el resultado). Cada frame queda guardado como una imagen con su `image_id`: desde ahí lo usás en generate_video como `first_frame` / `last_frame` con source "generada", o como referencia (source "generada") en generate_image y en un lote. Gratis (no hay IA), tarda unos segundos, no interrumpe (ADR-2026-09-11-el-agente-copia-un-video-con-sus-frames).\n' +
      'USALA sólo para armar una IMAGEN a partir de un cuadro, o cuando el usuario pide explícitamente un video NUEVO que no es ése. NUNCA para editar o copiar un video adjunto: ahí el video entero viaja como `reference_video` de generate_video (skill `copiar-un-video`), y mandar sus cuadros como referencias da un video parecido, no ese video. Pedí el primer cuadro de la toma que te interesa (y el último si querés un video A → B). No pidas frames "por las dudas": cada uno queda en el historial. NO la uses en vez de ver_video para entender el video (los cuadros no cuentan el ritmo ni el audio).\n' +
      'Con el frame en la mano tenés dos caminos (skill `copiar-un-video`): usarlo TAL CUAL como frame del video si la escena sirve como está, o EDITARLO con IA generando una imagen nueva con generate_image que lo lleve como referencia junto con el producto del usuario (misma composición, luz y encuadre; el producto reemplazado) y usar ESA imagen como frame.',
    input_schema: {
      type: 'object',
      properties: {
        archivo: {
          type: 'string',
          description: 'Nombre del video, tal cual figura en el contexto del adjunto. Omitilo si hay uno solo.',
        },
        tiempos: {
          type: 'array',
          items: { type: 'number' },
          description:
            'Los segundos de cada cuadro a sacar (ej. [0.5, 3, 7.2]), hasta 6. Un tiempo más allá del final se lleva al último instante del video.',
        },
      },
      required: ['tiempos'],
    },
  },
  {
    name: 'crear_pdf',
    description:
      'Arma un PDF y lo deja en el chat listo para descargar. Usalo cuando el usuario pide un PDF, un documento, un presupuesto, un informe, una ficha, un catálogo, una propuesta — o para devolverle una versión de un PDF que te adjuntó. Cargá la skill `pdf` antes: tiene el método para que se vea bien.\n' +
      'OJO con editar: no modificás el archivo del usuario, no lo tenemos. Leés el que adjuntó (ver_pdf) y creás uno NUEVO. Decíselo así, sin prometer que le tocaste el suyo.\n' +
      'El documento se arma con BLOQUES en orden. De estructura: "portada" (banda de color de borde a borde con `texto` = título, `subtexto` y `detalle`; siempre abre página), "banda" (encabezado de sección con fondo del acento, `texto`), "separador". De texto: "titulo", "subtitulo", "parrafo", "nota" (chica y gris, con `texto`), "lista" (`items` de strings). Con forma: "tarjetas" (grilla de cajas, `items` de {titulo, texto, cifra, url}, `columnas` 2 o 3), "cifras" (fila de números grandes, `items` de {valor, etiqueta}, hasta 4 por fila), "ficha" (foto al costado + `titulo`, `texto` y `detalle` como pastilla — la pieza para un producto: `url`, `lado`), "columnas" (`izquierda`, `derecha`, `titulos`), "etiquetas" (pastillas, `items` de strings), "destacado" (caja de color con `texto`: un total, una condición), "tabla" (`filas`, la primera es el encabezado; `estilo: "rayada"` alterna fondos, `encabezado: "solido"` lo pinta del acento; las columnas numéricas se alinean solas a la derecha) e "imagen" (`url`, `alto`, `align`). La paginación es automática.\n' +
      'ESTILOS: `color` del documento es el acento de toda la pieza — usá el de la marca del usuario si lo tenés. `pie` va al pie de cada página (ej. el nombre de la marca); `encabezado` arriba a la derecha desde la segunda página. En titulo, subtitulo, parrafo e imagen podés pasar `align`; en varios bloques un `color` propio.\n' +
      'IMÁGENES (en imagen, ficha y tarjetas): `url` tiene que ser una que YA tengas del contexto (la foto de un producto que viste con ver_productos, una imagen generada en este chat). NO inventes URLs ni uses imágenes de internet: si no se puede bajar, esa foto se saltea. Hasta 30 imágenes por documento.\n' +
      'Límites: NO entran emojis ni alfabetos no latinos; el texto de una tarjeta se corta a 6 líneas y el de una ficha a 8.\n' +
      'No cuesta créditos y no hay nada que confirmar: la card muestra la vista previa y el botón. Tu texto: UNA línea diciendo qué armaste. Devuelve `{ created, nombre, bloques, palabras }`.',
    input_schema: {
      type: 'object',
      properties: {
        nombre: {
          type: 'string',
          description: 'Nombre del archivo, sin extensión. Ej: "presupuesto-agosto".',
        },
        titulo: {
          type: 'string',
          description:
            'Título arriba de la primera página. Si el primer bloque es una portada, omitilo: el título va en ella.',
        },
        color: {
          type: 'string',
          description:
            'Color de acento en hex (#RRGGBB): portada, bandas, títulos, tarjetas, encabezado de tabla. Usá el de la marca si lo conocés.',
        },
        pie: {
          type: 'string',
          description: 'Línea del pie de página (ej. el nombre de la marca). El número de página va solo.',
        },
        encabezado: {
          type: 'string',
          description: 'Línea chica arriba a la derecha de cada página salvo la primera (ej. el nombre del documento).',
        },
        bloques: {
          type: 'array',
          description: 'El contenido, en orden.',
          items: {
            type: 'object',
            properties: {
              tipo: {
                type: 'string',
                enum: [
                  'portada',
                  'banda',
                  'titulo',
                  'subtitulo',
                  'parrafo',
                  'nota',
                  'lista',
                  'tabla',
                  'destacado',
                  'cifras',
                  'tarjetas',
                  'ficha',
                  'columnas',
                  'etiquetas',
                  'imagen',
                  'separador',
                ],
              },
              texto: {
                type: 'string',
                description: 'Para portada, banda, titulo, subtitulo, parrafo, nota, destacado y ficha.',
              },
              subtexto: { type: 'string', description: 'Para portada: la bajada debajo del título.' },
              detalle: {
                type: 'string',
                description:
                  'Para portada: línea chica (fecha, cliente, versión). Para ficha: la pastilla al pie (un precio, un SKU).',
              },
              titulo: { type: 'string', description: 'Para ficha: el título al lado de la foto.' },
              lado: {
                type: 'string',
                enum: ['izquierda', 'derecha'],
                description: 'Para ficha: de qué lado va la foto. Default izquierda.',
              },
              items: {
                type: 'array',
                description:
                  'Para lista y etiquetas: strings. Para cifras: {valor, etiqueta}. Para tarjetas: {titulo, texto, cifra, url}.',
                items: {
                  type: ['string', 'object'],
                  properties: {
                    valor: { type: 'string' },
                    etiqueta: { type: 'string' },
                    titulo: { type: 'string' },
                    texto: { type: 'string' },
                    cifra: { type: 'string' },
                    url: { type: 'string' },
                  },
                },
              },
              columnas: {
                type: 'number',
                enum: [2, 3],
                description: 'Para tarjetas: cuántas por fila. Default 3 (2 si hay dos o menos).',
              },
              izquierda: { type: 'string', description: 'Para columnas: el texto de la izquierda.' },
              derecha: { type: 'string', description: 'Para columnas: el texto de la derecha.' },
              titulos: {
                type: 'array',
                items: { type: 'string' },
                description: 'Para columnas: un título por columna (opcional).',
              },
              filas: {
                type: 'array',
                items: { type: 'array', items: { type: 'string' } },
                description: 'Para tabla. La primera fila es el encabezado.',
              },
              estilo: {
                type: 'string',
                enum: ['lineas', 'rayada'],
                description: 'Para tabla: "rayada" alterna el fondo de las filas. Default "lineas".',
              },
              encabezado: {
                type: 'string',
                enum: ['suave', 'solido'],
                description: 'Para tabla: "solido" pinta el encabezado del acento con texto blanco. Default "suave".',
              },
              url: {
                type: 'string',
                description:
                  'Para imagen y ficha: la URL de una foto que YA tengas del contexto. No inventes URLs.',
              },
              alto: {
                type: 'number',
                description: 'Para imagen: alto en puntos (máx 600). Omitilo y se acomoda solo.',
              },
              align: {
                type: 'string',
                enum: ['izquierda', 'centro', 'derecha'],
                description: 'Para titulo, subtitulo, parrafo, nota e imagen.',
              },
              color: {
                type: 'string',
                description: 'Hex propio para ese bloque (portada, banda, titulo, subtitulo, parrafo, destacado, etiquetas). Omitilo y usa el acento del documento.',
              },
            },
            required: ['tipo'],
          },
        },
      },
      required: ['nombre', 'bloques'],
    },
  },
  {
    name: 'ver_planilla',
    description:
      'Lee un TRAMO de filas de la planilla (Excel o CSV) que el usuario adjuntó. Al adjuntarla ya viste su FORMA (hojas, columnas, cuántas filas) y una muestra de las primeras: llamá esto sólo cuando necesites filas que no viste. No tiene UI, no cuesta créditos y no interrumpe.\n' +
      'Pedí tramos acotados y con criterio: para "sacá la columna precio" o "ordenalas por stock" NO hace falta leer 5.000 filas, alcanza con saber las columnas. Leé de verdad cuando tengas que mirar los datos (buscar un valor, detectar duplicados, entender qué hay en una columna rara).\n' +
      'Devuelve el texto de esas filas, numeradas con la fila REAL de la planilla (la 1 es el encabezado). Si la planilla ya no está disponible (el usuario recargó la página), te lo dice: ahí pedile que la vuelva a adjuntar.\n' +
      'Si adjuntó VARIAS, decí cuál en `archivo` (el nombre sale del resumen de cada adjunto). Sin `archivo` se lee la última.',
    input_schema: {
      type: 'object',
      properties: {
        archivo: {
          type: 'string',
          description:
            'Nombre de la planilla a leer, tal cual figura en su resumen ("[Planilla adjunta: <nombre>]"). Omitilo si hay una sola.',
        },
        hoja: {
          type: 'string',
          description:
            'Nombre de la hoja, o su número empezando en 1. Omitilo para la primera.',
        },
        desde: {
          type: 'number',
          description: 'Primera fila a leer (la 1 es el encabezado). Default 2.',
        },
        filas: {
          type: 'number',
          description: 'Cuántas filas leer (1 a 200). Default 50.',
        },
      },
    },
  },
  {
    name: 'crear_planilla',
    description:
      'Arma una PLANILLA y la deja en el chat lista para descargar (Excel .xlsx por defecto, o .csv). Usala cuando el usuario pide un Excel, un CSV, una tabla para bajar, un listado exportado — y también para DEVOLVERLE una planilla que te adjuntó con cambios hechos (filtrada, ordenada, con una columna nueva, limpia de duplicados).\n' +
      'OJO con editar: no modificás el archivo del usuario, no lo tenemos. Leés el que adjuntó (ver_planilla) y creás uno NUEVO con los cambios. Decíselo así, sin prometer que le tocaste el suyo.\n' +
      'Las `filas` son un array de arrays: la PRIMERA fila son los encabezados. Escribí los valores tal cual (números como números, sin separador de miles ni símbolo de moneda: "1999.5" y no "$ 1.999,50" — el usuario quiere poder sumar la columna). Los ceros a la izquierda y los teléfonos se respetan como texto.\n' +
      'No cuesta créditos y no hay nada que confirmar: la card muestra una vista previa y el botón de descargar. Tu texto: UNA línea diciendo qué armaste y que la puede bajar. Devuelve `{ created, nombre, formato, hojas, recortadas }`.',
    input_schema: {
      type: 'object',
      properties: {
        nombre: {
          type: 'string',
          description:
            'Nombre del archivo, sin extensión. Descriptivo: "productos-sin-stock", "ventas-agosto".',
        },
        formato: {
          type: 'string',
          enum: ['xlsx', 'csv'],
          description:
            'Default "xlsx". Usá "csv" sólo si el usuario lo pidió o si lo va a importar en otro sistema.',
        },
        hojas: {
          type: 'array',
          description:
            'Las hojas del archivo. Casi siempre UNA. En .csv sólo se baja la primera (un csv es una sola tabla).',
          items: {
            type: 'object',
            properties: {
              nombre: { type: 'string', description: 'Nombre de la hoja.' },
              filas: {
                type: 'array',
                description:
                  'Array de arrays. La primera fila son los encabezados. Todas las filas con la misma cantidad de columnas.',
                items: { type: 'array', items: { type: 'string' } },
              },
            },
            required: ['filas'],
          },
        },
      },
      required: ['nombre', 'hojas'],
    },
  },
  {
    name: 'mostrar_guardado',
    description:
      'Muestra en el chat una GALERÍA con cosas que el usuario YA TIENE guardadas, sin generar nada. Usala cuando pide VER lo suyo: "mostrame mis productos", "qué modelos tengo", "las fotos que te pasé", "tenés guardado el fondo del estudio?". Es de solo lectura y no cuesta créditos.\n' +
      'Tipos: "productos" (su catálogo), "modelos", "poses" (las propias que guardó), "fondos" (los propios), "subidas" (las fotos que él subió a este chat en algún momento). Para lo que GENERÓ (imágenes y videos) NO uses esta: usá show_recent_media, que además te las deja ver.\n' +
      'Si nombró algo puntual, pasalo en `buscar` y se filtra por nombre ("mostrame la remera negra" ⇒ tipo "productos", buscar "remera negra"). Si la lista está vacía la card lo dice sola: no inventes que tiene cosas guardadas.\n' +
      'Devuelve `{ shown: true, tipo, items }` y te deja un [registro:] con lo mostrado EN ORDEN y su id. Si después dice "usá el segundo" o "ese", mapealo a ese id y usalo directo como referencia — NUNCA le pidas el id (es técnico y no lo sabe). Tu texto: UNA línea corta ("Acá tenés tus productos"), sin listar de nuevo lo que ya se ve.',
    input_schema: {
      type: 'object',
      properties: {
        tipo: {
          type: 'string',
          enum: ['productos', 'modelos', 'poses', 'fondos', 'subidas'],
          description: 'Qué quiere ver.',
        },
        buscar: {
          type: 'string',
          description:
            'Filtro por nombre, si nombró algo puntual. Omitilo para mostrar todo lo que entre.',
        },
        limit: {
          type: 'number',
          description: 'Cuántos mostrar (default 12, máximo 24).',
        },
      },
      required: ['tipo'],
    },
  },
  {
    name: 'contact_support',
    description:
      'Muestra en el chat una tarjeta para que el usuario CONTACTE AL SOPORTE por WhatsApp con un mensaje ya redactado. Usala cuando el usuario tiene un PROBLEMA o error que vos no podés resolver desde el chat (bug, algo que no funciona, un cobro raro, la app trabada, etc.) y conviene que hable con una persona. El saludo del mensaje se arma solo con el nombre del usuario y su marca — vos SOLO escribís la descripción del problema en `problema` (en primera persona, como si lo escribiera el usuario: ej "tengo un problema al generar videos, me tira un error y no cierra el chat"). En mobile aparece un botón para abrir WhatsApp; en desktop, un QR para escanear. Devuelve `{ shown: true }`. No prometas que "ya avisaste a soporte": vos solo dejás el mensaje listo para que el usuario lo envíe.',
    input_schema: {
      type: 'object',
      properties: {
        problema: {
          type: 'string',
          description:
            'Descripción del problema EN PRIMERA PERSONA (la escribís vos por el usuario, va después del saludo automático). Clara y concreta: qué intentaba hacer, qué pasó, dónde. NO incluyas saludo ni "soy X de Y" — eso se agrega solo.',
        },
      },
      required: ['problema'],
    },
  },
  // ── Trabajo en LOTE (ADR-2026-09-08-el-chat-trabaja-en-lote) ─────────────
  {
    name: 'ver_fotos',
    description:
      'Te muestra CUALQUIER imagen del chat que no tengas a la vista, para que la MIRES antes de usarla. Pasá en `fotos` los ids, del tipo que sean — un producto del catálogo, una modelo guardada, una pose, un fondo, una creación tuya (`image_id`), una foto que el usuario subió suelta (el marcador de su mensaje dice su id) o una del montón —: vos pedís por id y el sistema busca dónde vive esa imagen. No tenés que saber de qué tipo es ni con qué parámetro se pide.\n' +
      'SIN `fotos` te muestra las primeras 10 del MONTÓN (7 fotos o más adjuntadas de una vez, que nunca viajaron en su mensaje), cada una con su `foto_id`; si son más, volvé a llamar con `desde` (10 → desde=11).\n' +
      'POR QUÉ mirar: casi todo lo que elegís por id lo elegís sin haberlo visto — una modelo guardada es un nombre en una lista, una pose es un nombre, y una foto que el usuario subió hace dos mensajes la viste una sola vez. Si la mandás a una generación sin mirarla, escribís el prompt y la `etiqueta` a ciegas o repitiendo lo poco que dijo el usuario. Cada imagen vuelve con QUÉ es y CÓMO se pasa a `reference_images`, así la usás en la misma ronda.\n' +
      'No tiene UI, no cuesta créditos y no interrumpe: llamala junto con otras cosas en la misma ronda. Lo que mirás se arrastra en todo el historial: pedí sólo lo que necesitás. Para ver TODAS las vistas de un producto está `ver_productos` (acá ves su foto principal); para una carpeta de Estilos, `ver_moodboard`. Para SUMAR un montón al catálogo NO hace falta mirarlo: agregar_productos con `lote_id` los agrupa solo y mejor.',
    input_schema: {
      type: 'object',
      properties: {
        fotos: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Los ids de las imágenes que querés ver, mezclando tipos si hace falta: `product_id`, `model_id`, `pose_id`, `fondo_id`, `image_id` de una creación, "m1-2" (foto suelta que subió, el id lo dice el marcador de su mensaje) o "f3" (del montón). Hasta 10. Sin esto, se muestra el tramo del montón.',
        },
        desde: {
          type: 'number',
          description:
            'Sólo sin `fotos`: desde qué foto del montón empezar, contando desde 1 (no desde 0). Default 1. Para el segundo tramo, 11.',
        },
        cantidad: {
          type: 'number',
          description: 'Sólo sin `fotos`: cuántas mostrar (1 a 10). Default 10.',
        },
      },
    },
  },
  {
    // ADR-2026-10-15-el-agente-abre-paginas-web: leer una página y traer sus imágenes, en los
    // tres motores (no es la `web_fetch` de Anthropic, que sólo trae texto y sólo en Claude).
    name: 'abrir_web',
    description:
      'Abre una PÁGINA WEB por su link (el que te pasó el usuario o uno de web_search) y te trae su título, descripción, el texto visible, los datos de producto que publica la tienda (nombre, precio, marca, SKU) y sus IMÁGENES principales, que ves debajo de su renglón. Cada imagen queda guardada como un ADJUNTO del chat con su `adjunto_id` ("web-…"): se usa igual que una foto que subió el usuario (reference_images con source "adjunto", ver_fotos, agregar_al_moodboard; para crear el producto, agregar_productos con `adjunto_ids`).\n' +
      'Gratis y sin confirmación. Con `imagenes: 0` sólo lee el texto. Las candidatas que no bajó vuelven en "otras imágenes": para traer alguna, llamala de nuevo con `urls_de_imagenes`. No entra a sitios con login (Instagram, Facebook: para eso están los scrapers) y una página que se arma en el navegador puede traer poco: si pasa, decilo.\n' +
      'Las imágenes de OTRA marca son de terceros: sirven de referencia o inspiración; no las presentes como fotos propias para publicar.',
    input_schema: {
      type: 'object',
      properties: {
        url: { type: 'string', description: 'El link de la página (o de una imagen).' },
        imagenes: { type: 'number', description: 'Cuántas imágenes traer y guardar (0 a 8). Default 6.' },
        urls_de_imagenes: {
          type: 'array',
          items: { type: 'string' },
          description: 'En vez de leer la página: las URLs de imágenes puntuales a traer (las de "otras imágenes" de una llamada anterior). Hasta 8.',
        },
      },
    },
  },
  {
    name: 'ver_productos',
    description:
      'Te muestra las FOTOS de hasta 12 productos del catálogo (llegan como imágenes dentro del resultado, cada producto con su product_id, nombre y QUÉ VISTAS tiene: frontal, trasera, lateral, detalle). Hasta 4 productos ves todas sus vistas; con más, la portada y la lista de vistas en texto. No tiene UI, no cuesta créditos y no interrumpe: la podés llamar junto con otras cosas en el mismo turno. Usala ANTES de escribir prompts cuando el nombre no te alcanza para describir el producto (color, material, forma, si es prenda o accesorio) y SIEMPRE antes de planear una sesión con poses: el motor sólo conoce los lados de la prenda de los que hay foto — un producto con la frontal sola no tiene espalda, y pedir "de espaldas" la hace inventar. Con lo que ves, escribí prompts concretos y coherentes con la referencia.',
    input_schema: {
      type: 'object',
      properties: {
        product_ids: {
          type: 'array',
          items: { type: 'string' },
          description: 'Ids del catálogo (los `id=` de la lista de PRODUCTOS). Máximo 12 por llamada.',
        },
      },
      required: ['product_ids'],
    },
  },
  {
    name: 'generar_lote_imagenes',
    description:
      'Genera MUCHAS imágenes de una vez (varios productos, varias variantes, varias escenas) en UNA sola tanda con UNA sola confirmación. Usala SIEMPRE que el pedido implique más de una llamada a generate_image: 2 o más productos, 2 o más variaciones distintas, "X imágenes de cada uno", "hacelos en otros colores", "todos mis productos en la playa". NUNCA emitas varias generate_image seguidas para eso: el usuario tendría que confirmar una por una.\n' +
      'Cada ÍTEM es una generación independiente: `etiqueta` (lo que el usuario ve en la lista: el nombre del producto o un rótulo corto como "Remera negra · azul"), `prompt` (mismas reglas que generate_image: personas = humanos reales fotorrealistas; el producto REAL viaja como referencia o el motor lo inventa), `cantidad` (1 a 6: la MISMA escena repetida N veces) y `reference_images` (mismo shape que en generate_image). Para "N imágenes DISTINTAS de cada producto" hacé N ítems por producto, cada uno con su prompt (frente / caminando / detalle…); para "N iguales" un ítem con cantidad N. Para "en otros colores": un ítem por color, con el producto como referencia y un prompt que pida "the exact product from the reference image, recolored to <color>, same shape, same design, same material". OJO: en un lote las fotos adjuntas del usuario NO entran solas — pasá source "adjunto" en los ítems que las necesiten.\n' +
      'SESIÓN COHERENTE (mismo lugar / misma modelo / mismo outfit en todas): el motor no recuerda entre ítems. Dos formas de anclar: (a) generá ANTES un ancla con generate_image, esperá su image_id y pasalo en cada ítem como source "generada"; o (b) DENTRO del mismo lote: ponele `id` a los ítems base y en los demás una referencia source "item" + `item_id` — el sistema genera primero las bases y después lo que depende de ellas, sin que vos esperes. En los dos casos la prenda sale SIEMPRE de las fotos del producto (el motor no conoce los lados de los que no hay foto — mirá las vistas con ver_productos antes de pedir poses de espaldas). Método completo en la skill `sesion-de-fotos`.\n' +
      'LOTES GRANDES, DE A SETS: el tope es 100 ítems y 100 imágenes por lote (1 crédito por imagen en 1K o 2K, 2 en 4K). No escribas 100 prompts en una sola llamada: se pierde calidad. Armá el lote de a SETS de hasta 18 ítems: la primera llamada con `cerrar: false` abre el lote y te devuelve su `lote_id` sin esperar nada; las siguientes con ese `lote_id` le SUMAN ítems; la última con `cerrar: true` lo cierra y ahí recién se le muestra al usuario el botón. Antes del primer set, contale el plan y el costo total una sola vez; entre sets no le hables. Un lote chico (hasta 18 ítems) va en una sola llamada, como siempre.\n' +
      'La card muestra la lista con el costo total (con más de 12 ítems, plegada: totales, formato, calidad y el botón); si espera el click o arranca sola depende de los PERMISOS del usuario (ver "QUÉ TENÉS HABILITADO AHORA"). Tu texto ANTES de cerrar el lote es el que invita a revisar o el que avisa que ya arrancó, según eso; en los dos casos avisá que tarda unos minutos, que puede cerrar la pestaña (el lote sigue en el servidor) y que las va a ver en Creaciones.\n' +
      'Devuelve `{ confirmed, image_ids, por_item: [{ etiqueta, product_id, prompt, image_ids }], fallidos }`. Lo que le contás al usuario al terminar sale de `por_item` y `fallidos` —cuántas salieron y de qué productos—, NUNCA del plan que tenías: si el lote salió con menos ítems de los que planeaste, decilo y ofrecé armar el resto. Con `por_item` seguís trabajando sobre lo generado sin volver a preguntar: "agarrá 5 de esos y hacelos en azul" = otro lote con esos product_id; "animá la de la remera" = ese image_id como source "generada".',
    input_schema: {
      type: 'object',
      properties: {
        titulo: {
          type: 'string',
          description:
            'Una línea que resume el lote para el usuario. Ej: "5 fotos de cada uno de tus últimos 10 productos".',
        },
        format: {
          type: 'string',
          description:
            'Formato de salida de TODO el lote (slug: post / square / story / portrait / banner). Default el último formato usado.',
        },
        lote_id: {
          type: 'string',
          description:
            'Para SUMAR ítems a un lote abierto: el `lote_id` que devolvió la llamada anterior. Omitilo para abrir uno nuevo.',
        },
        cerrar: {
          type: 'boolean',
          description:
            'Default true: el lote queda listo y se le muestra al usuario. false = "voy a seguir agregando sets": la llamada vuelve al instante con el `lote_id` y los totales, sin botón todavía.',
        },
        items: {
          type: 'array',
          description: 'Los ítems de este set (1 a 18 por llamada; hasta 100 en el lote). Cada uno es una generación.',
          items: {
            type: 'object',
            properties: {
              id: {
                type: 'string',
                description:
                  'OPCIONAL. Un nombre corto y único (letras, números, guiones: "base-campera", "v1") si OTROS ítems van a usar lo que salga de este como referencia. Sin id no se lo puede referenciar.',
              },
              etiqueta: {
                type: 'string',
                description:
                  'Rótulo corto que el usuario ve en la lista. Si el ítem es de un producto, su nombre (y la variación si hay: "Remera negra · rojo").',
              },
              prompt: {
                type: 'string',
                description: 'Prompt de la imagen, completo y específico (como en generate_image).',
              },
              cantidad: {
                type: 'number',
                description: 'Cuántas veces sale ESTE prompt (1 a 6, default 1). No lo infles.',
              },
              tarea: {
                type: 'string',
                enum: ['crear', 'editar', 'componer', 'grafica', 'explorar', 'cine'],
                description:
                  'Como en generate_image: "editar" si el ítem parte de una imagen que ya existe y le cambia una cosa (la pose, el fondo, un color) manteniendo el resto idéntico; "componer" si es un collage o una pieza con texto sobre fotos reales; "grafica" SÓLO si es un elemento gráfico simple de colores planos, estilo vectorial (ícono, lettering corto, sticker, patrón; sin degradados, texturas, 3D ni fotos) — una placa, un póster o un texto con efectos va a "componer"; "cine" si nada real tiene que verse, es un fotograma de película (manda la escena: lugar, luz, clima) y el ítem no lleva NINGUNA referencia; "explorar" si nada real tiene que verse y es ideación o inspiración de moda (looks, moodboard, dirección de arte, una persona inventada: manda la ropa o la persona) (en "grafica" y "explorar" las referencias son sólo de estilo; nunca con producto, modelo, logo ni pose); "crear" (default) si es una imagen nueva. "Esta foto en 5 poses" son CINCO ítems "editar", cada uno con esa foto como referencia (source "adjunto" o "generada") y un prompt que dice sólo qué cambia y que el resto queda igual — no una creación nueva por pose.',
              },
              edicion: {
                type: 'string',
                enum: ['conservar', 'fusionar'],
                description:
                  'Como en generate_image: "fusionar" sólo si el ítem mete en la imagen una PERSONA de otra imagen; con productos y todo lo demás, "conservar".',
              },
              piel_expuesta: {
                type: 'boolean',
                description: 'Como en generate_image: true si el ítem muestra mucha piel (bikini, lencería, escote pronunciado).',
              },
              fondo_blanco: {
                type: 'boolean',
                description: 'Como en generate_image: true = foto de catálogo en fondo blanco puro con corrección del punto blanco.',
              },
              luz_fondo_blanco: {
                type: 'string',
                enum: ['difusa', 'dura'],
                description: 'Sólo con fondo_blanco. Default "difusa".',
              },
              reference_images: {
                type: 'array',
                description:
                  'Referencias del ítem (máx 10): el producto REAL (source "producto" + product_id), modelo, logo, una imagen generada (source "generada" + image_id), una POSE guardada (source "pose" + pose_id), un FONDO guardado (source "fondo" + fondo_id), una foto que adjuntó el usuario (source "adjunto" — con `foto_id` si querés UNA puntual del montón que mandó, sin `foto_id` si son las pocas que ves en su mensaje), o LO QUE SALGA DE OTRO ÍTEM de este mismo lote (source "item" + item_id): ese ítem se genera antes y su primera imagen entra acá como referencia.\n' +
                  'Las referencias son POR ÍTEM: acá es donde una serie lleva UNA POSE DISTINTA POR FOTO — un `pose_id` en cada ítem. Lo que el usuario enganchó con el "+" NO entra solo en el lote (a diferencia de generate_image): si querés esas fotos en un ítem, pedilas con source "adjunto".',
                items: {
                  type: 'object',
                  properties: {
                    source: {
                      type: 'string',
                      enum: ['producto', 'modelo', 'logo', 'adjunto', 'generada', 'item', 'pose', 'fondo'],
                    },
                    product_id: { type: 'string' },
                    model_id: { type: 'string' },
                    image_id: { type: 'string' },
                    pose_id: {
                      type: 'string',
                      description:
                        'Con source "pose": id de una pose guardada. Uno distinto por ítem hace una serie con una pose por foto.',
                    },
                    fondo_id: {
                      type: 'string',
                      description: 'Con source "fondo": id de un fondo guardado.',
                    },
                    item_id: {
                      type: 'string',
                      description: 'Con source "item": el `id` del ítem base de este lote.',
                    },
                    foto_id: {
                      type: 'string',
                      description:
                        'Con source "adjunto": la foto puntual del montón que adjuntó el usuario ("f0", "f3"…, los ids que te da ver_fotos). Omitilo para usar las adjuntas del mensaje.',
                    },
                    etiqueta: {
                      type: 'string',
                      description:
                        'Como en generate_image: QUÉ ES una adjunta o generada ("Pose: caminando", "Producto: remera negra", "Persona: …", "Fondo: …", "Logo", "Plantilla: …", o un nombre libre como "Estampa a copiar"). Con un rol al principio, el motor la trata como si viniera del catálogo.',
                    },
                    imagen: {
                      type: 'integer',
                      description:
                        'Con source "adjunto" sin foto_id y varias adjuntas en el mensaje: a cuál va la etiqueta ("Imagen 2" → 2).',
                    },
                    adjunto_id: {
                      type: 'string',
                      description:
                        'Con source "adjunto": una imagen subida en un mensaje ANTERIOR, por el id del marcador de ese mensaje ("m1-2"). Viaja sin que la vuelva a adjuntar.',
                    },
                  },
                  required: ['source'],
                },
              },
            },
            required: ['prompt'],
          },
        },
      },
      required: ['items'],
    },
  },
  {
    name: 'agregar_productos',
    description:
      'Suma UNO o VARIOS productos al catálogo del usuario a partir de fotos. Tres fuentes: (1) las FOTOS que el usuario adjuntó a su mensaje (las ves como bloques de imagen; no pases nada, la card las toma sola — también si las adjuntó en un mensaje anterior); (2) IMÁGENES GENERADAS en este chat (pasá sus `image_ids`, ej. "agregá la segunda que generaste como producto"); (3) un LOTE grande de fotos que el usuario adjuntó (te llega como "lote_id=…" en su mensaje → pasá `lote_id`). Usala cuando pide agregar / sumar / cargar / guardar algo como producto, sea una foto o cuarenta. NO la uses para fotos que adjuntó sólo como referencia de una imagen a generar.\n' +
      'Singular vs plural: "agregámelo / sumalo como producto" ⇒ `un_solo_producto: true` (todas las fotos son del mismo producto, hasta 4). Varios productos o no está claro ⇒ omitilo: la card los agrupa con IA (las fotos del mismo producto juntas, hasta 4 por producto) y sugiere nombres. Si el usuario dijo cómo se llama, pasalo en `nombre`.\n' +
      'La card muestra la propuesta (fotos, nombre editable). Si el usuario tiene que tocar Crear o se crean solos depende de sus PERMISOS (ver "QUÉ TENÉS HABILITADO AHORA"); en los dos casos la categorización es automática y no cuesta créditos. Esa línea va ANTES de llamar la tool.\n' +
      'Devuelve `{ created, cantidad, productos: [{ product_id, nombre, fotos }], fallidos, fuera_por_plan }` (o `{ created: false, motivo: "sin_fotos" }` si no había fotos: ahí pedile que las adjunte con el "+"). OJO: cuando leés ese resultado con `created: true` los productos YA ESTÁN en el catálogo — contá cuáles quedaron; pedirle ahí que "toque Crear" es pedirle algo que ya pasó. Los product_id son del catálogo: si después pide imágenes de esos productos, usalos directo como reference_images (source "producto") — no lo hagas elegir de nuevo.',
    input_schema: {
      type: 'object',
      properties: {
        image_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Ids de imágenes GENERADAS en este chat que pasan a ser las fotos del producto. Omitilo si las fotos son las que adjuntó el usuario.',
        },
        lote_id: {
          type: 'string',
          description: 'El id del lote de fotos que el usuario adjuntó (viene en su mensaje). Omitilo si no hay lote.',
        },
        adjunto_ids: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Fotos puntuales por su adjunto_id: las que trajiste con abrir_web ("web-…") o extraer_fotos_del_pdf ("pdf-…"), o las de un mensaje anterior ("m1-2"). Omitilo si son las del último mensaje.',
        },
        un_solo_producto: {
          type: 'boolean',
          description:
            'true = todas las fotos son de UN mismo producto (el usuario habló en singular). Omitir = agrupar con IA.',
        },
        nombre: {
          type: 'string',
          description: 'Nombre del producto si el usuario lo dijo (sólo con un_solo_producto). Si no, omitilo: lo sugiere la IA.',
        },
        mensaje: {
          type: 'string',
          description:
            'Una línea que aparece arriba de la card, escrita ANTES de que se cree nada. Con confirmación: "Revisá el nombre y tocá Crear". Sin confirmación (permiso en directo): "Los sumo a tu catálogo".',
        },
      },
    },
  },
  {
    name: 'guardar_datos_del_producto',
    description:
      'Guarda en la ficha de UN producto del catálogo los datos que leíste (una ficha técnica, un informe de fábrica, una planilla) o que te dictaron. Quedan en el producto para siempre y los usan los modos, la infografía y la tienda online. No tiene UI, no cuesta créditos. Con el modo Catálogo abierto usá cargar_datos_de_productos.\n' +
      'Tres cosas distintas, no las mezcles: `medidas` = las del PRODUCTO; `paquete` = las de la CAJA en que se envía UNA unidad (nunca la caja máster con varias unidades: esa no sirve); `campos` = características ("Voltaje" / "7,2 V", "Material" / "Roble"). Guardá sólo lo que está escrito; un dato dudoso, preguntalo. Las medidas y el paquete que confirmó la persona no se pisan sin `sobrescribir`; una característica con la misma clave SÍ se reemplaza, así que si la persona la había dicho distinta, preguntá antes. Te dice qué no se guardó: contalo.',
    input_schema: {
      type: 'object',
      properties: {
        producto_id: { type: 'string', description: 'Id del producto (el de ver_productos, o el product_id que devolvió agregar_productos).' },
        medidas: {
          type: 'object',
          description: 'Medidas del PRODUCTO.',
          properties: {
            unidad: { type: 'string', enum: ['cm', 'mm', 'm'] },
            alto: { type: 'number' },
            ancho: { type: 'number' },
            profundidad: { type: 'number' },
            espesor: { type: 'number' },
            peso_kg: { type: 'number' },
          },
        },
        campos: {
          type: 'array',
          description: 'Características: hasta 20, clave corta y valor como lo dice la fuente.',
          items: { type: 'object', properties: { clave: { type: 'string' }, valor: { type: 'string' } }, required: ['valor'] },
        },
        paquete: {
          type: 'object',
          description: 'Caja de UNA unidad para el envío: medidas en cm y peso en kg.',
          properties: { alto: { type: 'number' }, ancho: { type: 'number' }, profundidad: { type: 'number' }, peso_kg: { type: 'number' } },
        },
        sobrescribir: { type: 'boolean', description: 'true sólo si la persona pidió cambiar datos que ya había confirmado.' },
      },
      required: ['producto_id'],
    },
  },
];

/**
 * `buscar_en_el_chat` — NO está en `V2_AGENT_TOOLS` a propósito.
 *
 * Sólo se le manda al modelo una vez que la conversación se COMPACTÓ
 * (ADR-2026-09-13-el-chat-se-compacta-y-se-puede-buscar): antes de eso el historial
 * entero está en contexto y una herramienta para "buscar en el chat" sólo lo invitaría a
 * llamarla al pedo. Por eso tampoco entra en el MAPA del system prompt (que es estable y
 * cacheado): el aviso de que existe va en el bloque volátil, el mismo turno en que
 * aparece. La resuelve `executeTool` en `useV2Chat.ts`, en el cliente y gratis.
 */
export const HERRAMIENTA_BUSCAR_EN_EL_CHAT: V2Tool = {
  name: 'buscar_en_el_chat',
  description:
    'Busca palabras en los mensajes VIEJOS de esta conversación, los que ya no llegan textuales porque se resumieron. El chat completo sigue guardado: esto lee el original, no el resumen.\n' +
    'USALA cuando el usuario se refiere a algo de antes y el resumen no alcanza: "lo que te dije al principio", "la campaña que armamos", "el producto que te pasé", un precio, un nombre, una decisión, por qué descartaron algo. También cuando necesitás un id que el resumen no trajo.\n' +
    'NO la uses para lo que está a la vista en los últimos mensajes, ni como reemplazo de preguntarle algo que nunca dijo: si no encuentra nada, te lo dice y ahí sí preguntale.\n' +
    'Es silenciosa, instantánea y gratis (no sale del navegador): llamala en la misma ronda que otras herramientas. Devuelve recortes textuales con la posición de cada mensaje (#3/120 = el tercero de ciento veinte).',
  input_schema: {
    type: 'object',
    properties: {
      consulta: {
        type: 'string',
        description:
          'Las palabras a buscar, no una pregunta. Poné los términos distintivos (el nombre del producto, de la campaña, un id, un color) y no las palabras de relleno. Ej: "campaña invierno camperas cuero".',
      },
      limite: {
        type: 'number',
        description: 'OPCIONAL. Cuántos mensajes traer, de 1 a 10. Por defecto 5.',
      },
    },
    required: ['consulta'],
  },
};
